/* eslint-disable import/extensions */
/** @module Auth0WhiteLabelAssetHelper */

import Auth0Cdn from './auth0-cdn-resource-resolver.mst.mjs';
import Auth0UtilsBrowser from '../../utils/auth0-utils-browser.mjs';
import Auth0DynamicScriptLoader from '../../utils/auth0-dynamic-script-loader.mjs';
import Auth0Localizer from '../../utils/auth0-localization-helper.mjs';

/**
 * Maps legacy `white_label_*` prefixed keys to their canonical short-name equivalents.
 * Both forms are accepted everywhere. Short names take precedence on collision.
 * @type {Record<string, string>}
 */
export const LEGACY_KEY_MAP = {
  white_label_app_name: 'app_name',
  white_label_logo_url: 'logo_url',
  white_label_icon_url: 'icon_url',
  white_label_css: 'css',
  white_label_marketing_button_display_text: 'marketing_button_display_text',
  white_label_marketing_button_url: 'marketing_button_url',
  white_label_app_support_email: 'app_support_email',
  white_label_app_login_support_email: 'app_login_support_email',
  white_label_app_login_support_phone_number: 'app_login_support_phone_number',
  white_label_favicon_16: 'favicon_16',
  white_label_favicon_32: 'favicon_32',
  white_label_apple_touch_icon: 'apple_touch_icon',
  white_label_mask_icon: 'mask_icon',
  // deprecated color/style props — still functional, use css instead
  white_label_page_background_color: 'page_background_color',
  white_label_form_background_color: 'form_background_color',
  white_label_form_text_color: 'form_text_color',
  white_label_login_button_background_color: 'login_button_background_color',
  white_label_login_button_text_color: 'login_button_text_color',
  white_label_marketing_button_background_color: 'marketing_button_background_color',
  white_label_marketing_button_border_color: 'marketing_button_border_color',
  white_label_marketing_button_text_color: 'marketing_button_text_color',
  // ENGINE-10929: opt-out escape hatch — apps not yet ready for the native SSAS
  // change-password flow (flow=changePassword) can keep sending their own destination here.
  // See _forgotPasswordLink in auth0-login-page-helper.mst.mjs.
  white_label_forgot_password_link: 'forgot_password_link',
};

/** All valid short-name white-label keys — use to pre-filter before calling normalizeWhiteLabelOptions */
export const KNOWN_WHITE_LABEL_KEYS = new Set(Object.values(LEGACY_KEY_MAP));

const DEPRECATED_KEYS = new Set([
  'page_background_color',
  'form_background_color',
  'form_text_color',
  'login_button_background_color',
  'login_button_text_color',
  'marketing_button_background_color',
  'marketing_button_border_color',
  'marketing_button_text_color',
]);

/**
 * Normalizes a raw options object to canonical short-name keys.
 * Accepts both short names (e.g. `logo_url`) and legacy aliases (e.g. `white_label_logo_url`).
 * Short names win on collision. Unknown keys pass through unchanged.
 * @param {object} options
 * @returns {object} Normalized options with short-name keys
 */
export const normalizeWhiteLabelOptions = (options = {}) => {
  // First pass: map legacy keys to short names (short names take precedence)
  const normalized = Object.entries(options).reduce((acc, [key, value]) => {
    const shortKey = LEGACY_KEY_MAP[key] ?? key;
    if (!(shortKey in acc)) {
      acc[shortKey] = value;
    }
    return acc;
  }, {});
  // Second pass: ensure explicit short-name keys override any legacy alias that was written first
  Object.entries(options).forEach(([key, value]) => {
    if (Object.values(LEGACY_KEY_MAP).includes(key)) {
      // explicit short-name key present — it wins unconditionally
      normalized[key] = value;
    }
  });
  return normalized;
};

/**
 * Manages white label asset resolution for Auth0 authentication pages.
 *
 * Accepts options using **canonical short-name keys** (preferred) or legacy `white_label_*`
 * aliases (still accepted for backward compatibility — normalized on intake).
 *
 * ## First-class props (permanent)
 * | Short name                      | Legacy alias                                    | Description |
 * |---------------------------------|-------------------------------------------------|-------------|
 * | `app_name`                      | `white_label_app_name`                          | Brand name, e.g. "ShipEngine" |
 * | `logo_url`                      | `white_label_logo_url`                          | Full logo URL; max 400×200px recommended |
 * | `icon_url`                      | `white_label_icon_url`                          | Square icon URL; max 512×512px; used for spinner + favicon fallback |
 * | `css`                           | `white_label_css`                               | URL to custom CSS loaded after default styles on every page |
 * | `marketing_button_display_text` | `white_label_marketing_button_display_text`     | Toolbar "Get Started" button text |
 * | `marketing_button_url`          | `white_label_marketing_button_url`              | Toolbar "Get Started" button URL |
 * | `app_support_email`             | `white_label_app_support_email`                 | General support email |
 * | `app_login_support_email`       | `white_label_app_login_support_email`           | Login-specific support email |
 * | `app_login_support_phone_number`| `white_label_app_login_support_phone_number`    | Login-specific support phone |
 * | `favicon_16`                    | `white_label_favicon_16`                        | 16×16 favicon URL |
 * | `favicon_32`                    | `white_label_favicon_32`                        | 32×32 favicon URL |
 * | `apple_touch_icon`              | `white_label_apple_touch_icon`                  | 180×180 Apple touch icon URL |
 * | `mask_icon`                     | `white_label_mask_icon`                         | Monochrome SVG for Safari pinned tab |
 *
 * ## Deprecated props (still functional — use `css` instead)
 * `page_background_color`, `form_background_color`, `form_text_color`,
 * `login_button_background_color`, `login_button_text_color`,
 * `marketing_button_background_color`, `marketing_button_border_color`,
 * `marketing_button_text_color`
 */
class Auth0WhiteLabelAssetHelper {
  constructor(options = {}) {
    console.log(`Auth0WhiteLabelAssetHelper => ${JSON.stringify(options)}`);
    this._whiteLabelOptions = normalizeWhiteLabelOptions(options);
    this._deprecationWarned = new Set();
    const resolved = Auth0UtilsBrowser.tenantOrDomainResolver(options.tenantOrDomain);
    this._scriptLoader = new Auth0DynamicScriptLoader(resolved.tenant.id);

    // Warn once at instantiation time if any deprecated props are present
    if (this.isDeprecatedPropSet) {
      const present = [...DEPRECATED_KEYS].filter(k => this._whiteLabelOptions[k] != null);
      console.warn(`[Auth0WhiteLabelAssetHelper] Deprecated white label props detected: ${present.join(', ')}. `
        + 'Use the "css" prop to provide a custom stylesheet instead. '
        + 'These props will be removed in a future release.');
    }
  }

  /**
   * Returns true if any deprecated cosmetic color/style prop is present.
   */
  get isDeprecatedPropSet() {
    return [...DEPRECATED_KEYS].some(k => this._whiteLabelOptions[k] != null);
  }

  get brandLogoUrl() {
    return this._whiteLabelOptions?.logo_url
      ? this._whiteLabelOptions.logo_url
      : Auth0Cdn.AUTH0_LOGO_URL;
  }

  get brandIconUrl() {
    return this._whiteLabelOptions?.icon_url
      ? this._whiteLabelOptions.icon_url
      : Auth0Cdn.AUTH0_LOGO_ICON_URL;
  }

  get brandWaitingSpinnerIconUrl() {
    return this._whiteLabelOptions?.icon_url
      ? this._whiteLabelOptions.icon_url
      : Auth0Cdn.AUTH0_WAIT_SPINNER_URL;
  }

  get appName() {
    return this._whiteLabelOptions?.app_name || 'ShipStation';
  }

  get appSupportEmail() {
    return this._whiteLabelOptions?.app_support_email || 'support@shipstation.com';
  }

  get appLoginSupportEmail() {
    return this._whiteLabelOptions?.app_login_support_email || 'loginsupport@shipstation.com';
  }

  get appLoginSupportPhoneNumber() {
    return this._whiteLabelOptions?.app_login_support_phone_number || '+1 (888) 893-3885';
  }

  get gettingStartedText() {
    return Auth0Localizer.getLocalizedText('toolbar-action-get-started', { appName: this.appName });
  }

  get gettingStartedUrl() {
    return this._whiteLabelOptions.marketing_button_url || 'https://www.shipstation.com/step1/';
  }

  get marketingButtonDisplayText() {
    return this._whiteLabelOptions?.marketing_button_display_text;
  }

  get cssUrl() {
    return this._whiteLabelOptions?.css;
  }

  get whiteLabelOptions() {
    return this._whiteLabelOptions;
  }

  // ── Deprecated color/style getters ──────────────────────────────────────
  // Still functional for backward compatibility. Use the css prop instead.

  /** @deprecated Use the css prop to set background color via CSS variable --wl-page-bg */
  get pageBackgroundColor() {
    return this._whiteLabelOptions?.page_background_color;
  }

  /** @deprecated Use the css prop to set background color via CSS */
  get formBackgroundColor() {
    return this._whiteLabelOptions?.form_background_color;
  }

  /** @deprecated Use the css prop to set text color via CSS */
  get formTextColor() {
    return this._whiteLabelOptions?.form_text_color;
  }

  /** @deprecated Use the css prop to style the login button via CSS */
  get loginButtonBackgroundColor() {
    return this._whiteLabelOptions?.login_button_background_color;
  }

  /** @deprecated Use the css prop to style the login button via CSS */
  get loginButtonTextColor() {
    return this._whiteLabelOptions?.login_button_text_color;
  }

  /** @deprecated Use the css prop to style the marketing button via CSS */
  get marketingButtonBackgroundColor() {
    return this._whiteLabelOptions?.marketing_button_background_color;
  }

  /** @deprecated Use the css prop to style the marketing button via CSS */
  get marketingButtonBorderColor() {
    return this._whiteLabelOptions?.marketing_button_border_color;
  }

  /** @deprecated Use the css prop to style the marketing button via CSS */
  get marketingButtonTextColor() {
    return this._whiteLabelOptions?.marketing_button_text_color;
  }

  // ── Methods ──────────────────────────────────────────────────────────────

  loadCustomStyles = async () => {
    if (!this.cssUrl) return;

    await this._scriptLoader.loadStyles(this.cssUrl, true);
  };

  setFavIcons = () => {
    // these must exist on the page and will not be added completely dynamically
    const favicon16 = document.getElementById('favicon16');
    const favicon32 = document.getElementById('favicon32');
    const appleTouchIcon = document.getElementById('apple-touch-icon');
    const maskIcon = document.getElementById('mask-icon');
    const favIconSet = [favicon16, favicon32, appleTouchIcon, maskIcon];

    // remove
    favIconSet.forEach(ele => ele?.parentNode.removeChild(ele));

    const cacheBust = `${new Date().getTime()}`;

    favicon16.href = this._whiteLabelOptions?.favicon_16
      ? `${this._whiteLabelOptions?.favicon_16}?cb=${cacheBust}`
      : `${Auth0Cdn.AUTH0_FAVICON_16_URL}?cb=${cacheBust}`;
    favicon32.href = this._whiteLabelOptions?.favicon_32
      ? `${this._whiteLabelOptions?.favicon_32}?cb=${cacheBust}`
      : `${Auth0Cdn.AUTH0_FAVICON_32_URL}?cb=${cacheBust}`;
    appleTouchIcon.href = this._whiteLabelOptions?.apple_touch_icon
      ? `${this._whiteLabelOptions?.apple_touch_icon}?cb=${cacheBust}`
      : `${Auth0Cdn.AUTH0_APPLE_TOUCH_ICON_URL}?cb=${cacheBust}`;
    maskIcon.href = this._whiteLabelOptions?.mask_icon
      ? `${this._whiteLabelOptions?.mask_icon}?cb=${cacheBust}`
      : `${Auth0Cdn.AUTH0_MASK_ICON_URL}?cb=${cacheBust}`;

    // add them back
    favIconSet.forEach(ele => document.head.appendChild(ele));
  };
}

export default Auth0WhiteLabelAssetHelper;
